import {
  ADMIN_THEME_QA_STORAGE_KEY,
  applyThemePreference,
  createAuthComponentsPlugin,
  publishEmbedOrg,
  readStoredThemePreference,
  setupFilterBar,
  setupMenuDividers,
  setupPageScroll,
  setupTableHost,
  setupTableXScroll,
  setupTooltip,
} from "@hereby/components";
import { reportSessionAdoption } from "@hereby/components/analytics";
import { createAlpinePlugin } from "@hereby/sdk";
import Alpine from "alpinejs";
import { adminAppComponent } from "./app";
import { chatRecoveryPage } from "./chat-recovery";
import { appGuideSendModal, onboardingPage } from "./onboarding";
import { orgSettingsAnchors } from "./org-settings-anchors";
import { createRouterStore } from "./router";
import { scheduledChangesPage } from "./scheduled-changes";
import { schedulerComponent } from "./scheduler";
import {
  inviteAcceptComponent,
  signupFormComponent,
  signupVerifyComponent,
  workspaceHubComponent,
} from "./signup";
import "./styles/main.css";

// 경영 대시보드 디자인 검토용 강제 데모 모드 캡처 (임시).
// admin 라우터가 탭 이동 시 pushState로 쿼리스트링(?demo=1)을 버리므로,
// 최초 로드 시점에 sessionStorage로 옮겨 두고 embedUrlFor가 그걸 읽는다.
// sticky: ?demo=1로 켜면 탭 세션 동안 유지(새로고침에도 안 꺼짐), ?demo=0으로만 끈다.
{
  const demo = new URLSearchParams(window.location.search).get("demo");
  if (demo === "1") {
    sessionStorage.setItem("hereby_demo", "1");
  } else if (demo === "0") {
    sessionStorage.removeItem("hereby_demo");
  }
}

// 테마 — 관리자 앱은 사용자에게 전환 기능을 주지 않아 늘 라이트다. 다크 스타일 확인은 개발 · QA 전용 키
// (localStorage "hereby.theme.admin-qa" = "dark")로만 켠다. 직원 앱의 사용자 선택("hereby.theme")은 운영에서
// 같은 출처라 함께 보이므로 읽지 않는다 — 읽으면 Works 에서 고른 다크가 관리자 앱으로 샌다.
applyThemePreference(readStoredThemePreference(ADMIN_THEME_QA_STORAGE_KEY));

Alpine.plugin(
  createAlpinePlugin({
    onSessionAdopted: reportSessionAdoption("admin"),
    appId: "admin",
    baseUrl: import.meta.env.API_BASE_URL,
  }),
);
Alpine.plugin(createAuthComponentsPlugin());
Alpine.store("router", createRouterStore());
Alpine.store("publicAssets", { base: import.meta.env.BASE_URL });

// Design-system toast store. Faithful to the toast spec in
// hereby-design (top-center, 3s auto-close, success/error/info variants).
// Usage anywhere: this.$store.toast.success("저장되었습니다")
interface ToastItem {
  id: number;
  variant: "success" | "error" | "info";
  message: string;
}
export interface ToastStore {
  items: ToastItem[];
  seq: number;
  push(variant: ToastItem["variant"], message: string, autoClose?: number | false): number;
  success(message: string, autoClose?: number | false): number;
  error(message: string, autoClose?: number | false): number;
  info(message: string, autoClose?: number | false): number;
  dismiss(id: number): void;
}
const toastStore: ToastStore = {
  items: [],
  seq: 0,
  push(variant, message, autoClose = 3000) {
    if (!message) {
      return -1;
    }
    const id = ++this.seq;
    this.items.push({ id, variant, message });
    if (autoClose !== false) {
      setTimeout(() => this.dismiss(id), autoClose);
    }
    return id;
  },
  success(message, autoClose) {
    return this.push("success", message, autoClose);
  },
  error(message, autoClose) {
    return this.push("error", message, autoClose);
  },
  info(message, autoClose) {
    return this.push("info", message, autoClose);
  },
  dismiss(id) {
    const i = this.items.findIndex((t) => t.id === id);
    if (i !== -1) {
      this.items.splice(i, 1);
    }
  },
};
// 공통 스크롤 방식 — 페이지 전체가 스크롤하고 탭 줄은 sticky, 표는 남은 화면 높이 안에서 스크롤(@hereby/components page-scroll)
Alpine.directive("page-scroll", (el, _dir, { cleanup }) => {
  cleanup(setupPageScroll(el as HTMLElement));
});
Alpine.store("toast", toastStore);
Alpine.data("_adminApp", adminAppComponent);
Alpine.data("schedulerComponent", schedulerComponent);
Alpine.data("onboardingPage", onboardingPage);
Alpine.data("scheduledChangesPage", scheduledChangesPage);
Alpine.data("chatRecoveryPage", chatRecoveryPage);
Alpine.data("orgSettingsAnchors", orgSettingsAnchors);
Alpine.data("appGuideSendModal", appGuideSendModal);
Alpine.data("signupForm", signupFormComponent);
Alpine.data("signupVerify", signupVerifyComponent);
Alpine.data("workspaceHub", workspaceHubComponent);
Alpine.data("inviteAccept", inviteAcceptComponent);
Alpine.start();

// Singleton hover tooltip — shared across apps (see @hereby/components setupTooltip).
// Anchors LNB collapsed menus, table/org action icons, truncated labels, and every
// <select> / .ms-dropdown__trigger (compact tooltip with the selected value).
setupTooltip();
// 드롭다운 메뉴의 빈 구분선 정리 — 조건부 항목이 통째로 숨어
// 항목 없이 선만(때로는 두세 줄) 남는 것을 걷어낸다.
setupMenuDividers();

// 표 가로 스크롤바 — 가로로 넘치는 표의 아래쪽에 드래그 가능한 바를 붙인다
// (see @hereby/components setupTableXScroll). PC·태블릿(≥768px)만, 모바일은 no-op.
// MutationObserver 기반이라 Alpine이 나중에 그리는 표·모달 안 표까지 자동 커버.
setupTableXScroll();

// 표를 직접 품은 스크롤 래퍼에 `.has-table` — 앱 CSS 가 카드 outline 을 그리는 판정 근거
// (see @hereby/components setupTableHost). CSS `:has(> table)` 대신이다: 그 규칙 하나가
// 문서 어디서든 DOM 이 바뀔 때마다 문서 전체 스타일 재계산을 일으킨다.
setupTableHost();

// 목록 화면 상단 「필터 + 검색·기능」 한 줄의 반응형 동작
// (see @hereby/components setupFilterBar). 전환선은 px 미디어쿼리가 아니라 실제
// 내용 폭 측정이다 — 같은 1280px 이라도 필터·버튼이 적은 화면은 한 줄로 남는다.
setupFilterBar();

// 임베드된 앱(hr-system·team-manager…)이 "너 어느 조직이냐"고 물으면 답한다.
// 개발에서는 앱마다 포트가 달라 세션이 따로 놀아, 바깥은 A 조직인데 iframe 안은
// B 조직 데이터를 보여줄 수 있다 — 그걸 자식이 감지해 경고하도록 하는 쪽의 절반.
// 오가는 값은 조직 id·이름뿐이라 토큰은 창을 건너지 않는다.
publishEmbedOrg(() => {
  const u = Alpine.store("hereby")?.currentUser as
    | { organizationId?: string; organizationName?: string }
    | null
    | undefined;
  return u?.organizationId
    ? { organizationId: u.organizationId, organizationName: u.organizationName ?? "" }
    : null;
});
